/**
 * Smart-list MCP server — display constants for the frontend.
 *
 * Mirrors `workers/mcp-smart-lists/wrangler.toml` `[vars]` (prices, allowances)
 * and `workers/mcp-smart-lists/src/server.js` (tool names/descriptions). The
 * worker is the source of truth for what actually happens; everything here is
 * display copy only and must be changed together with those files, never
 * independently.
 *
 * MCP_TOOLS and MCP_EXAMPLE_PROMPTS reach BOTH surfaces — the public /mcp page (server.json's
 * websiteUrl, so its readers are strangers) and the in-app modal, which render the same
 * McpConnectPanel — so both use the worker's outward vocabulary ("stock screens").
 * The tool NAMES are the wire-level MCP identifiers — they say "smart_list" and cannot change
 * without breaking every connected client.
 */

export const MCP_URL = 'https://mcp.quantape.com/mcp';

// Where the in-app "learn more" links point (public, works logged-out).
export const MCP_DOCS_PATH = '/mcp';

// The /mcp page's <h1> and browser <title>, and the What's New card that links there. One
// string, because label and destination have to agree: the announcement card kept the old
// "Smart lists in your AI assistant" for weeks after the page itself was reframed (issue #310),
// which is what two hand-written copies of one title always eventually do.
export const MCP_PAGE_TITLE = 'Stock screens and market data in your AI assistant';

// The one pitch for the MCP server, rendered by BOTH surfaces that sell it: the public /mcp page
// (at body size, as its lead) and the in-app modal (inside McpConnectPanel). One string, because
// two hand-written variants of one sentence is exactly the drift this file's header warns about —
// and the short in-app variant this replaced named only lists, underselling get_my_watchlist and
// get_market_overview. Outward vocabulary ("stock screens"), since server.json's websiteUrl points
// strangers at /mcp; app users read the in-app term everywhere else.
export const MCP_PITCH =
  "Put QuantApe's stock screens, per-stock insights, your own watchlist and today's market conditions inside the " +
  'assistant you already use. Connect ChatGPT, Claude, Cursor, VS Code, Gemini CLI or any other ' +
  'assistant that speaks the Model Context Protocol (MCP), and ask which screens a ticker is in, ' +
  'what changed overnight, or for a screen with its earnings-reaction, expectations and signal ' +
  'analytics — and ' +
  'let it cross-reference them for you.';

// name/description/price mirror server.js's registerFreeTool/registerMeteredTool
// calls; price is null for the two always-free tools.
export const MCP_TOOLS = [
  {
    name: 'list_smart_lists',
    description: 'Browse QuantApe\'s stock screens by category.',
    price: null,
  },
  {
    name: 'get_list_criteria',
    description: 'Metadata for one stock screen — name, description, category.',
    price: null,
  },
  {
    name: 'get_smart_list',
    description: 'Symbols currently in a stock screen.',
    price: 0.01,
  },
  {
    name: 'get_smart_list_metrics',
    description: 'Per-symbol earnings-reaction, expectations, implied-move and technical-signal analytics for a screen (no quotes or valuation ratios).',
    price: 0.05,
  },
  {
    name: 'get_stock_insights',
    description: "Everything QuantApe derives about one stock: earnings reaction and drift, what's priced in, call guidance, signals, why it moved, sector mood and rotation, which screens hold it. No quotes.",
    price: 0.1,
  },
  {
    name: 'get_smart_list_changes',
    description: 'Symbols added to / removed from a screen since its last recorded baseline.',
    price: 0.02,
  },
  {
    name: 'find_symbol_lists',
    description: 'Which stock screens currently contain a given ticker.',
    price: 0.01,
  },
  {
    name: 'get_my_watchlist',
    description: 'Your own watchlist with the same analytics and the latest headlines per symbol (signed in only).',
    price: 0.05,
  },
  {
    name: 'get_market_overview',
    description: "Today's market read: daily overview, fear & greed (market and per sector), sector rotation and market-cycle phase, market-news analysis. Pick sections to keep it small.",
    price: 0.02,
  },
];

// Mirrors ALLOWANCE_ANON / ALLOWANCE_USER / ALLOWANCE_POWER in wrangler.toml [vars].
export const MCP_ALLOWANCES = { anon: 5, user: 10, power: 50 };

/**
 * Per-client connect examples, in the order the UI lists them. ChatGPT and
 * Claude are given equal billing (same connector-settings flow) rather than
 * either one standing in as "the" way to connect; Cursor/VS Code point back
 * at the generic `mcpServers` JSON block instead of duplicating it, and
 * Gemini CLI gets its own one-liner like Claude Code does.
 */
const CONNECTOR_INSTRUCTIONS = 'Settings → Connectors → Add custom connector → paste the endpoint above.';

export const MCP_CLIENT_EXAMPLES = [
  { client: 'ChatGPT', instructions: CONNECTOR_INSTRUCTIONS },
  {
    client: 'Claude',
    instructions: CONNECTOR_INSTRUCTIONS,
    commandLabel: 'Or, Claude Code:',
    command: `claude mcp add --transport http quantape ${MCP_URL}`,
  },
  { client: 'Cursor / VS Code', instructions: 'Use the client config JSON above.' },
  { client: 'Gemini CLI', command: `gemini mcp add --transport http quantape ${MCP_URL}` },
];

// Kept for callers that just want "a" connect command (e.g. the landing
// page's single monospace line uses the endpoint itself instead — see
// McpConnectPanel). Never present this as "the" way to connect on its own.
export const MCP_CONNECT_COMMAND = MCP_CLIENT_EXAMPLES.find((c) => c.command)?.command;

export function mcpConnectCommand(client = 'Claude') {
  const entry = MCP_CLIENT_EXAMPLES.find((c) => c.client === client);
  return entry?.command || MCP_CONNECT_COMMAND;
}

/**
 * Concrete example prompts, tuned to what the tools actually do — shown to make the "ask it
 * things" pitch tangible rather than abstract. Order matters: the landing page's closer card
 * shows only the first two. These render on the PUBLIC /mcp page as well as the in-app modal,
 * so they use the outward term where they name the product ("stock screens"); naming an
 * individual screen ("the contrarian value list") reads the same to both audiences.
 */
export const MCP_EXAMPLE_PROMPTS = [
  'What\'s going on with my watchlist today?',
  'How is the market doing today, and how is my watchlist doing against it?',
  'What does QuantApe know about NVDA — earnings reaction, what\'s priced in, guidance, signals?',
  'Which stock screens is NVDA in right now?',
  'Show me the oversold reversal list and tell me which names also have a golden cross.',
  'What moved into or out of the short-squeeze candidates since the last rebuild?',
  'Give me the contrarian value list with each name\'s last earnings reaction and how much of the next report is already priced in.',
  'Compare the high-alpha small-cap list with the momentum-breakout list and tell me what overlaps.',
];

/**
 * Generic `mcpServers` config block accepted by most desktop/IDE MCP clients
 * (Claude Desktop, Cursor, VS Code, Windsurf, …). Adds an Authorization
 * header only when a session token is supplied.
 */
export function mcpDesktopConfig(token) {
  const server = { type: 'http', url: MCP_URL };
  if (token) {
    server.headers = { Authorization: `Bearer ${token}` };
  }
  return JSON.stringify({ mcpServers: { quantape: server } }, null, 2);
}
